/** Admin row for the management {@link DataTable}. */
import * as React from 'react';
import { User } from 'lucide-react';
import { Button } from '@/components/forms/Button';
import { LabelBadge } from '@/components/common/LabelBadge';
import { TableRow } from '@/components/common/data-table';
import { STAFF_STATUS_CONFIG } from '@/components/admins/admin-configs';
import { deriveAdminContact, resolveAdminPermission } from './admin-table-utils';
import type { Admin } from '@/types/admin.types';
import { normalizeStaffAccountStatus } from '@/types/user.types';
import type { InterfaceElementResponseDto } from '@/types/interface-elements.types';
import { useRoleConfig } from '@/hooks/useRoleConfig';
import { toRoleColorCss } from '@/utils/role-label-utils';

/** Props for {@link AdminRow}. */
interface AdminRowProps {
  admin: Admin;
  interfaceElements: InterfaceElementResponseDto[];
  onView?: (id: string) => void;
  onEdit?: (id: string) => void;
  onDelete?: (id: string) => void;
  pageActions?: { canEditRow?: boolean; canDeleteRow?: boolean };
}

/** Admin row for the management {@link DataTable}. */
export const AdminRow: React.FC<AdminRowProps> = ({
  admin,
  interfaceElements,
  onView,
  onEdit,
  onDelete,
  pageActions,
}) => {
  const statusKey = normalizeStaffAccountStatus(String(admin.status));
  const statusConfig = STAFF_STATUS_CONFIG[statusKey] || STAFF_STATUS_CONFIG.active;
  const StatusIcon = statusConfig.icon;
  const roleConfig = useRoleConfig();

  const adminRoles = admin.userRoles?.map((ur) => ur.role.code) || [];
  const contact = deriveAdminContact(admin);

  const canEdit = React.useMemo(
    () =>
      (pageActions?.canEditRow ?? true) &&
      resolveAdminPermission(admin.userRoles, interfaceElements, 'edit-role'),
    [pageActions?.canEditRow, admin.userRoles, interfaceElements],
  );

  const canDelete = React.useMemo(
    () =>
      (pageActions?.canDeleteRow ?? true) &&
      resolveAdminPermission(admin.userRoles, interfaceElements, 'delete-role'),
    [pageActions?.canDeleteRow, admin.userRoles, interfaceElements],
  );

  return (
    <TableRow>
      <td>
        <div className="flex items-center gap-2">
          <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-[color-mix(in_srgb,var(--color-1-nuance-1)_10%,transparent)]">
            <User className="w-4 h-4 text-[var(--color-1-nuance-1)]" />
          </div>
          <span className="font-medium text-foreground">
            {admin.firstName} {admin.lastName}
          </span>
        </div>
      </td>

      <td>
        <div className="flex flex-col gap-0.5">
          <span className="flex items-center gap-2 text-sm text-foreground">
            <contact.icon
              className="w-4 h-4 shrink-0"
              style={{ color: 'var(--color-1-nuance-1)' }}
            />
            {contact.value}
          </span>
          {contact.secondaryValue && (
            <span className="text-xs text-muted-foreground">{contact.secondaryValue}</span>
          )}
        </div>
      </td>

      <td>
        <div className="flex flex-wrap gap-1">
          {adminRoles.map((roleCode) => {
            const config = roleConfig[roleCode.toUpperCase()];
            const RoleIcon = config?.icon;
            const badgeColor = config?.colorClass ? toRoleColorCss(config.colorClass) : undefined;
            return (
              <LabelBadge
                key={roleCode}
                label={config?.label ?? roleCode}
                icon={RoleIcon}
                variant="outlined"
                color={badgeColor}
                size="md"
              />
            );
          })}
        </div>
      </td>

      <td>
        <LabelBadge
          label={statusConfig.label}
          icon={StatusIcon}
          variant={statusConfig.badgeVariant}
          color={statusConfig.badgeColor}
          size="md"
        />
      </td>

      <td className="text-center">
        <div className="flex items-center justify-center gap-1">
          <Button preset="row-view" onClick={() => onView?.(admin.id)} />

          {canEdit && <Button preset="row-edit" onClick={() => onEdit?.(admin.id)} />}

          {canDelete && <Button preset="row-delete" onClick={() => onDelete?.(admin.id)} />}
        </div>
      </td>
    </TableRow>
  );
};
